import * as React from 'react';
import { useMemo, useCallback } from 'react';
import { Select } from '@rendering/components';
import { useQuery } from '@tanstack/react-query';
import { FinishData, getFinishList } from 'src/services/materialClient';
import { getSdk } from 'src/sdk';

interface FinishSelectorProps {
    id: string;
    finishes: string[];
    onSelect: (id: string, finishes: FinishData[]) => void;
}

const FinishSelector = (props: FinishSelectorProps) => {
    const { id, finishes, onSelect } = props;
    const { data: finishList } = useQuery({
        queryKey: ['finishList'],
        queryFn: () => getFinishList(getSdk()),
    });

    const { finishOptions, finishDictionary } = useMemo(() => {
        const options: { label: string; value: string }[] = [];
        const dictionary: Record<string, FinishData> = {};

        finishList?.forEach((finish) => {
            options.push({ label: finish.name, value: finish.id });
            dictionary[finish.id] = finish;
        });

        return { finishOptions: options, finishDictionary: dictionary };
    }, [finishList]);

    const onChange = useCallback(
        (selected: any) => {
            if (!finishDictionary) return;

            let selectedVals = [];
            if (!Array.isArray(selected)) {
                selectedVals = [finishDictionary[selected.value]];
            } else {
                selectedVals = selected.map((option) => finishDictionary[option.value]);
            }

            onSelect(id, selectedVals);
        },
        [id, finishDictionary, onSelect],
    );

    const selectedFinishes = useMemo(
        () => finishOptions?.filter((finish) => finishes.includes(finish.value)),
        [finishOptions, finishes],
    );

    return (
        <div data-testid='finish-selector'>
            <Select
                label='Finishes'
                value={selectedFinishes.length > 0 ? selectedFinishes : { label: 'None', value: '' }}
                options={finishOptions}
                onChange={onChange}
                isMulti={selectedFinishes.length > 0}
            />
        </div>
    );
};

export default FinishSelector;
